import React from 'react'
import { Link } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Chip } from '../../components/ui/Chip'
import { Button } from '../../components/ui/Button'
import { apiFetch } from '../../lib/api-client'

interface Property {
  id: string
  address: string
  name: string | null
  postnr: string | null
  postnrnavn: string | null
  serial_number: number | null
  serial_label: string
  status: string
  lease_count: number
}

function statusVariant(s: string): 'green' | 'amber' | 'gray' | 'red' {
  switch (s) {
    case 'active': return 'green'
    case 'inactive': return 'gray'
    case 'pending': return 'amber'
    default: return 'gray'
  }
}

function statusLabel(s: string): string {
  switch (s) {
    case 'active': return 'Aktiv'
    case 'inactive': return 'Inaktiv'
    case 'pending': return 'Afventer'
    default: return s
  }
}

const COLS = ['Løbenr.', 'Navn', 'Postnr. & By', 'Lejemål', 'Status']

export function PropertyListPage() {
  const [properties, setProperties] = React.useState<Property[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    apiFetch<{ items: Property[]; total: number }>('/api/v1/properties')
      .then((r) => setProperties(r.items))
      .catch(() => setError('Kunne ikke hente ejendomme. Prøv igen.'))
      .finally(() => setLoading(false))
  }, [])

  return (
    <AppLayout
      actions={
        <Link to="/ejendomme/opret">
          <Button size="sm">
            <Plus size={13} style={{ marginRight: 4 }} />
            Opret ejendom
          </Button>
        </Link>
      }
    >
      <div style={{
        background: '#ffffff',
        border: '1px solid #e8eaed',
        borderRadius: 10,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 24px 12px',
          borderBottom: '1px solid #e8eaed',
        }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
            Ejendomme {!loading && <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 13 }}>({properties.length})</span>}
          </span>
        </div>

        {loading && (
          <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Henter…</div>
        )}

        {error && (
          <div style={{ padding: 20, color: '#dc2626', fontSize: 13 }}>{error}</div>
        )}

        {!loading && !error && properties.length === 0 && (
          <div style={{ padding: 56, textAlign: 'center' }}>
            <p style={{ color: '#9ca3af', fontSize: 13, marginBottom: 16 }}>
              Ingen ejendomme endnu. Opret din første ejendom for at komme i gang.
            </p>
            <Link to="/ejendomme/opret">
              <Button size="sm">
                <Plus size={13} style={{ marginRight: 4 }} />
                Opret ejendom
              </Button>
            </Link>
          </div>
        )}

        {!loading && !error && properties.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {COLS.map(h => (
                  <th
                    key={h}
                    style={{
                      padding: '10px 24px',
                      textAlign: 'left',
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #e8eaed',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {properties.map((p, i) => (
                <PropertyRow key={p.id} property={p} isLast={i === properties.length - 1} />
              ))}
            </tbody>
          </table>
        )}

        {!loading && !error && properties.length > 0 && (
          <div style={{ padding: '10px 24px', borderTop: '1px solid #e8eaed', fontSize: 12, color: '#9ca3af' }}>
            Viser {properties.length} af {properties.length} resultater
          </div>
        )}
      </div>
    </AppLayout>
  )
}

function PropertyRow({ property: p, isLast }: { property: Property; isLast: boolean }) {
  const [hovered, setHovered] = React.useState(false)

  return (
    <tr
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? '#f9fafb' : 'transparent',
        transition: 'background 0.1s',
        borderBottom: isLast ? 'none' : '1px solid #f3f4f6',
        cursor: 'pointer',
      }}
    >
      <td style={{ padding: '12px 24px' }}>
        <Link to={`/ejendomme/${p.id}`} style={{ textDecoration: 'none', display: 'block' }}>
          <span style={{
            fontFamily: 'monospace',
            fontSize: 12,
            fontWeight: 600,
            color: '#4f46e5',
            background: '#eff6ff',
            padding: '2px 6px',
            borderRadius: 4,
          }}>
            {p.serial_label}
          </span>
        </Link>
      </td>
      <td style={{ padding: '12px 24px' }}>
        <Link to={`/ejendomme/${p.id}`} style={{ textDecoration: 'none' }}>
          <div style={{ fontWeight: 500, color: '#111827' }}>{p.name ?? p.address}</div>
          {p.name && (
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{p.address}</div>
          )}
        </Link>
      </td>
      <td style={{ padding: '12px 24px', color: '#6b7280' }}>
        {p.postnr && p.postnrnavn
          ? `${p.postnr} ${p.postnrnavn}`
          : <span style={{ color: '#d1d5db' }}>—</span>}
      </td>
      <td style={{ padding: '12px 24px', color: '#6b7280' }}>
        {p.lease_count > 0
          ? `${p.lease_count} lejemål`
          : <span style={{ color: '#d1d5db' }}>—</span>}
      </td>
      <td style={{ padding: '12px 24px' }}>
        <Chip variant={statusVariant(p.status)}>{statusLabel(p.status)}</Chip>
      </td>
    </tr>
  )
}
